import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_URL } from "../../consts";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AnalyticsDialog = ({ open, onClose, spaceId, spaceData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const token = useSelector((state) => state.token);
  
  const [timePeriod, setTimePeriod] = useState('24h'); // '24h', 'week', 'month'
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [viewsCount, setViewsCount] = useState(0);
  const [viewsData, setViewsData] = useState({ labels: [], datasets: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch real analytics data
  useEffect(() => {
    if (open && spaceId && token) {
      fetchAnalyticsData();
    }
  }, [open, spaceId, timePeriod, token]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${API_URL}/spaces/authenticated/spaces/${spaceId}/analytics`,
        {
          params: { time_period: timePeriod },
          withCredentials: true, // SECURITY: Cookies are sent automatically
        }
      );

      const { subscriber_count, views_count, views_over_time = [] } = response.data;
      
      setSubscriberCount(subscriber_count || 0);
      setViewsCount(views_count || 0);

      // Format views data for chart
      let labels = [];
      let data = [];

      if (views_over_time && views_over_time.length > 0) {
        if (timePeriod === '24h') {
          // Last 24 hours - hourly data
          labels = views_over_time.map((item) => {
            const date = new Date(item.date);
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          });
          data = views_over_time.map((item) => item.count || 0);
        } else if (timePeriod === 'week') {
          // Last 7 days - daily data
          labels = views_over_time.map((item) => {
            const date = new Date(item.date);
            return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
          });
          data = views_over_time.map((item) => item.count || 0);
        } else if (timePeriod === 'month') {
          // Last 30 days - daily data
          labels = views_over_time.map((item) => {
            const date = new Date(item.date);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          });
          data = views_over_time.map((item) => item.count || 0);
        }
      }

      setViewsData({
        labels,
        datasets: [
          {
            label: 'Views',
            data,
            borderColor: 'rgb(25, 118, 210)',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            tension: 0.4,
            fill: true,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(25, 118, 210)',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
          },
        ],
      });
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError(err?.response?.data?.error || 'Failed to load analytics data');
      // Fallback to showing views_count from spaceData if available
      if (spaceData?.views_count !== undefined) {
        setViewsCount(spaceData.views_count);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTimePeriodChange = (event, newPeriod) => {
    if (newPeriod !== null) {
      setTimePeriod(newPeriod);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: 500,
          },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 600,
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: function(context) {
            return `Views: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: isMobile ? 45 : 0,
          minRotation: isMobile ? 45 : 0,
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11,
          },
          precision: 0,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BarChartIcon color="primary" />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Analytics
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <>
            {/* Subscriber Count Card */}
            <Card
              sx={{
                mb: 3,
                background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.05) 100%)',
                border: '1px solid rgba(25, 118, 210, 0.12)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Total Subscribers
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {subscriberCount.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Views Count Card */}
            <Card
              sx={{
                mb: 3,
                background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.08) 0%, rgba(76, 175, 80, 0.05) 100%)',
                border: '1px solid rgba(46, 125, 50, 0.12)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: 'success.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <BarChartIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Total Views
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {viewsCount.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </>
        )}

        {!loading && !error && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Views Over Time
            </Typography>

            {/* Time Period Selector */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <ToggleButtonGroup
                value={timePeriod}
                exclusive
                onChange={handleTimePeriodChange}
                aria-label="time period"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 2,
                    py: 1,
                    border: '1px solid rgba(25, 118, 210, 0.3)',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="24h" aria-label="24 hours">
                  24 Hours
                </ToggleButton>
                <ToggleButton value="week" aria-label="week">
                  Week
                </ToggleButton>
                <ToggleButton value="month" aria-label="month">
                  Month
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Chart Container */}
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
              }}
            >
              <Box sx={{ height: isMobile ? '300px' : '400px', position: 'relative' }}>
                {viewsData.labels.length > 0 ? (
                  <Line data={viewsData} options={chartOptions} />
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No view data available for this period</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AnalyticsDialog;

